import {
  ArrowBendRightDown,
  ArrowBendRightUp,
  ArrowsDownUp,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { breakpoints } from 'src/hooks/useBreakpoint';
import { Checkbox } from './Checkbox';
import { Label } from './Label';
import { Loading } from './Loading';
import { SelectBox } from './SelectBox';

type Align = 'left' | 'center' | 'right';

export interface Header {
  key: string;
  label: string;
  width?: string;
  disableSorting?: boolean;
  wrapValue?: boolean;
  align?: Align;
}

export interface Row {
  [key: string]: string | number | React.ReactElement;
}

interface TableProps {
  headers: Header[];
  rows: Row[];
  selectedRow?: number;
  onRowClick?: (index: number) => void;
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number, perPage: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSort: (key: string | any, direction: string) => void;
  loading: boolean;
  defaultSize?: number;
  disablePagination?: boolean;
  setSize?: (size: number) => void;
  fullWidth?: boolean;
  // Row selection props
  enableRowSelection?: boolean;
  selectedRows?: number[];
  onRowSelectionChange?: (selectedRows: number[]) => void;
  onSelectAllRows?: (selectAll: boolean) => void;
  sortConfig?: { key: string | null; direction: string | null };
}

const Table = ({
  headers,
  rows,
  selectedRow,
  totalItems,
  currentPage,
  onPageChange,
  onSort,
  onRowClick,
  loading,
  defaultSize = 10,
  disablePagination = false,
  setSize = () => {},
  fullWidth = true,
  enableRowSelection = false,
  selectedRows = [],
  onRowSelectionChange,
  onSelectAllRows,
  sortConfig: initialSortConfig = { key: null, direction: null },
}: TableProps) => {
  const location = useLocation();

  const [isMobile, setIsMobile] = useState(false);
  const [sortConfig, setSortConfig] = useState(initialSortConfig);

  const [perPage, setPerPage] = useState<number>(defaultSize);
  const [paginationDisabled, setPaginationDisabled] = useState<boolean>(false);
  const [internalSelectedRows, setInternalSelectedRows] = useState<number[]>(
    [],
  );

  const currentSelectedRows = onRowSelectionChange
    ? selectedRows
    : internalSelectedRows;
  const setCurrentSelectedRows =
    onRowSelectionChange || setInternalSelectedRows;

  const handleSort = (key: string | null) => {
    let direction = 'asc';

    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    setSortConfig({ key, direction });

    if (key) {
      onSort(key, direction);
    }
  };

  const handleRowSelection = (rowIndex: number, checked: boolean) => {
    const newSelectedRows = checked
      ? [...currentSelectedRows, rowIndex]
      : currentSelectedRows.filter((index) => index !== rowIndex);

    setCurrentSelectedRows(newSelectedRows);
  };

  const handleSelectAll = (checked: boolean) => {
    if (onSelectAllRows) {
      onSelectAllRows(checked);
    } else {
      const newSelectedRows = checked ? rows.map((_, index) => index) : [];
      setCurrentSelectedRows(newSelectedRows);
    }
  };

  const isAllSelected =
    rows.length > 0 && currentSelectedRows.length === rows.length;
  const isIndeterminate =
    currentSelectedRows.length > 0 && currentSelectedRows.length < rows.length;

  const totalPages = Math.ceil(totalItems / perPage);

  const renderSortIcon = (key: string | null) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowBendRightDown />
    ) : (
      <ArrowBendRightUp />
    );
  };

  const renderSortableIndicator = (header: Header) => {
    if (header.disableSorting) return null;

    if (sortConfig.key === header.key) {
      return renderSortIcon(header.key);
    }

    return <ArrowsDownUp className="opacity-60" />;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page, perPage);
    }
  };

  const handlePerPageChange = (value: string) => {
    setSize(Number(value));
    setPerPage(Number(value));
    onPageChange(1, Number(value));
  };

  const renderPagination = () => {
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 bg-neutral-0 text-neutral-500 rounded disabled:opacity-50 hover:bg-neutral-50"
        >
          Previous
        </button>

        {pages.map((page) => (
          <button
            key={page}
            onClick={() => handlePageChange(page)}
            className={`px-4 py-2 ${
              currentPage === page
                ? 'bg-primary-100 text-primary hover:bg-primary-200'
                : 'bg-neutral-0 hover:bg-neutral-50'
            } rounded`}
          >
            {page}
          </button>
        ))}

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 bg-neutral-0 text-neutral-500 rounded disabled:opacity-50 hover:bg-neutral-50"
        >
          Next
        </button>
      </div>
    );
  };

  useEffect(() => {
    setIsMobile(window.innerWidth < breakpoints.tablet);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < breakpoints.tablet);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (totalPages <= 1 || disablePagination) {
      setPaginationDisabled(true);
    } else {
      setPaginationDisabled(false);
    }
  }, [totalPages, disablePagination]);

  return (
    <div className={clsx(fullWidth && 'w-full')}>
      {loading ? (
        <div className="text-center py-4">
          <Loading size={'8'} />
        </div>
      ) : (
        <>
          {!isMobile && (
            <div className="overflow-x-auto">
              <table
                className={clsx(
                  'border-collapse border border-neutral-100',
                  fullWidth && 'w-full',
                )}
              >
                <thead className="bg-neutral-50">
                  <tr>
                    {enableRowSelection && (
                      <th className="px-4 py-2 border-b border-neutral-100 text-neutral-600 font-medium text-[14px] w-12">
                        <div className="flex items-center justify-center">
                          <Checkbox
                            checked={isAllSelected}
                            indeterminate={isIndeterminate}
                            onChange={handleSelectAll}
                            ariaLabel="Select all rows"
                          />
                        </div>
                      </th>
                    )}
                    {headers.map((header) => (
                      <th
                        key={header.key}
                        className={clsx(
                          'px-4 py-2 border-b border-neutral-100 text-neutral-600 font-medium text-[14px] whitespace-nowrap',
                          header.align === 'center'
                            ? 'text-center'
                            : header.align === 'right'
                              ? 'text-right'
                              : 'text-left',
                          !header.disableSorting && 'cursor-pointer',
                        )}
                        onClick={() =>
                          !header.disableSorting && handleSort(header.key)
                        }
                        style={{
                          width: header.width && `${header.width}`,
                          maxWidth: header.width && `${header.width}`,
                        }}
                      >
                        <div
                          className={clsx(
                            'flex items-center',
                            header.align === 'center'
                              ? 'justify-center'
                              : header.align === 'right'
                                ? 'justify-end'
                                : 'justify-start',
                          )}
                        >
                          {header.label} {renderSortableIndicator(header)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      onClick={() => onRowClick?.(rowIndex)}
                      className={clsx(
                        'border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50 text-neutral-600',
                        selectedRow === rowIndex && 'bg-neutral-50',
                        onRowClick && 'cursor-pointer',
                      )}
                    >
                      {enableRowSelection && (
                        <td className="px-4 py-2 w-12">
                          <div className="flex items-center justify-center">
                            <Checkbox
                              checked={currentSelectedRows.includes(rowIndex)}
                              onChange={(checked) =>
                                handleRowSelection(rowIndex, checked)
                              }
                              onClick={(e) => e.stopPropagation()}
                              ariaLabel={`Select row ${rowIndex + 1}`}
                            />
                          </div>
                        </td>
                      )}
                      {headers.map((header) => (
                        <td
                          key={header.key}
                          className={clsx(
                            'px-4 py-2',
                            header.align === 'center'
                              ? 'text-center'
                              : header.align === 'right'
                                ? 'text-right'
                                : 'text-left',
                            header.wrapValue === false && 'whitespace-nowrap',
                          )}
                          style={{
                            width: header.width ?? undefined,
                            maxWidth: header.width ?? undefined,
                          }}
                        >
                          {typeof row[header.key] === 'object'
                            ? (row[header.key] as JSX.Element)
                            : row[header.key]}
                        </td>
                      ))}
                    </tr>
                  ))}

                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          enableRowSelection
                            ? headers.length + 1
                            : headers.length
                        }
                        className="text-center py-4"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {isMobile && (
            <div className="flex flex-col">
              {rows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className="flex flex-col border border-neutral-200 rounded mb-4"
                >
                  {enableRowSelection && (
                    <div className="flex justify-between px-2 py-2 border-b">
                      <span className="font-semibold">Select</span>
                      <Checkbox
                        checked={currentSelectedRows.includes(rowIndex)}
                        onChange={(checked) =>
                          handleRowSelection(rowIndex, checked)
                        }
                        ariaLabel={`Select row ${rowIndex + 1}`}
                      />
                    </div>
                  )}
                  {headers.map((header) => (
                    <div
                      key={header.key}
                      className="flex justify-between px-2 py-2 border-b last:border-none"
                    >
                      <span className="font-semibold">{header.label}</span>
                      <span>{row[header.key]}</span>
                    </div>
                  ))}
                </div>
              ))}

              {rows.length === 0 && (
                <div className="text-center py-4">No data available</div>
              )}
            </div>
          )}

          {!paginationDisabled && (
            <div className="flex py-2 justify-end">{renderPagination()}</div>
          )}

          {!paginationDisabled && (
            <div className="flex py-2 justify-end align-center items-center">
              <Label htmlFor="perPage" className="mr-2 mt-3">
                Items per page:
              </Label>

              <SelectBox
                options={[5, 10, 20, 30].map((value) => ({
                  value: String(value),
                }))}
                defaultValue={{ value: String(perPage) }}
                variant="outlined"
                color="primary"
                size="medium"
                onChange={handlePerPageChange}
                className="w-10"
                direction="up"
                key={`${rows.length}-${perPage}`}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Table;
